import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { strings } from '@singparty/shared';
import { sessionApi } from '@/lib/sessionApi';
import { ApiClientError } from '@/lib/apiClient';
import { acquireWakeLock, wakeLockSupported } from '@/lib/wakeLock';
import { tvBrowserSupported } from '@/lib/featureDetect';
import { TvStage } from '@/components/tv/TvStage';
import { Button } from '@/components/ui/Button';
import { Logo } from '@/components/ui/Logo';

/**
 * TV client (05 §2.7b). Launch → claim (one-gesture autoplay unlock, EC:A1) →
 * TvStage runs the YouTube player + playback authority state machine (Phase 3).
 */
export const Route = createFileRoute('/tv/$sid')({
  validateSearch: z.object({ st: z.string().optional() }),
  component: TvClient,
});

function TvClient() {
  const { sid } = Route.useParams();
  const { st } = Route.useSearch();
  const [started, setStarted] = useState(false);

  // EC:A4 — very old TV browser lacking required APIs → notice, not a broken UI.
  if (!tvBrowserSupported()) {
    return (
      <div className="tv-theme flex min-h-screen flex-col items-center justify-center bg-bg px-8 text-center">
        <h1 className="mb-4 text-3xl font-bold">{strings.tv.oldBrowser}</h1>
        <p className="text-text-secondary">This TV browser is too old to run the player.</p>
      </div>
    );
  }

  if (!started) return <Launch sid={sid} st={st} onStarted={() => setStarted(true)} />;
  return <TvStage sid={sid} />;
}

function Launch({ sid, st, onStarted }: { sid: string; st?: string; onStarted: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function start() {
    setBusy(true);
    setError(null);
    try {
      await sessionApi.claim(st); // sets sp_screen, advances epoch (04 §1.5)
      void acquireWakeLock(); // within the user gesture (EC:A2)
      try {
        await document.documentElement.requestFullscreen?.();
      } catch {
        /* fullscreen optional */
      }
      window.history.replaceState({}, '', `/tv/${sid}`); // strip the one-time token (02 §7)
      onStarted();
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not start');
      setBusy(false);
    }
  }

  return (
    <div className="tv-theme flex min-h-screen flex-col items-center justify-center bg-bg px-8 text-center">
      <Logo className="mb-4 w-120" />
      <p className="mb-10 text-xl text-text-secondary">{strings.tagline}</p>
      <Button size="lg" autoFocus loading={busy} onClick={start} className="px-12 py-8 text-3xl">
        {strings.tv.startPlayer}
      </Button>
      <p className="mt-6 text-sm text-text-secondary">{strings.tv.fullscreenHint}</p>
      {!wakeLockSupported() && <p className="mt-2 text-sm text-amber-300">{strings.tv.wakeLockHint}</p>}
      {error && <p className="mt-4 text-lg text-danger">{error}</p>}
    </div>
  );
}
